import React, { useState, useEffect, createContext, useContext } from 'react';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { spacing, borderRadius, fontSize, fontWeight } from '../../styles/theme';
import { getKmfcClient } from '../../lib/supabase';

const SystemStatusContext = createContext();

export const useSystemStatus = () => {
  const context = useContext(SystemStatusContext);
  if (!context) {
    throw new Error('useSystemStatus must be used within SystemStatusProvider');
  }
  return context;
};

export const SystemStatusProvider = ({ children }) => {
  const [systemStatus, setSystemStatus] = useState({
    isLoading: false,
    hasConnection: true,
    error: null,
    lastCheck: null
  });

  // Simple system check - no alerts
  const performSystemCheck = async () => {
    try {
      setSystemStatus(prev => ({ ...prev, isLoading: true, error: null }));
      
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) {
        // Geen database verbinding is ok - het kan zijn dat user niet ingelogd is
        setSystemStatus({
          isLoading: false,
          isOnline: true,
          lastCheck: new Date(),
          error: null
        });
        return;
      }

      // Test connection
      await kmfcClient.from('opening_hours').select('id').limit(1);

      setSystemStatus({
        isLoading: false,
        isOnline: true,
        lastCheck: new Date(),
        error: null
      });
    } catch (error) {
      console.error('System check failed:', error);
      setSystemStatus({
        isLoading: false,
        isOnline: false,
        lastCheck: new Date(),
        error: error.message
      });
    }
  };

  // Initial check and 30-minute interval
  useEffect(() => {
    // Perform initial check
    performSystemCheck();

    // Set up 30-minute interval
    const interval = setInterval(performSystemCheck, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, []);

  const value = {
    systemStatus,
    performSystemCheck
  };

  return (
    <SystemStatusContext.Provider value={value}>
      {children}
    </SystemStatusContext.Provider>
  );
};
